import React, { useCallback, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';

import { CoreViewer } from './CoreViewer';
import styles from './MultiCoreViewer.module.scss';
import {
	CoreLaneRowConfiguration,
	CoreLaneRowType,
	IntervalLaneRowConfiguration,
} from './components/CoreLane/CoreLaneRows';
import {
	CreateIntervalFn,
	UpdateIntervalFn,
} from './components/CoreLane/CoreLaneRows/IntervalLaneRow/IntervalDataContext';
import { IntervalDataProvider } from './components/CoreLane/CoreLaneRows/IntervalLaneRow/IntervalDataProvider';
import {
	IntervalLaneRowProvider,
	IntervalsData,
} from './components/CoreLane/CoreLaneRows/IntervalLaneRow/IntervalLaneRowProvider';
import { useCoreViewerContext } from './context/CoreViewerContext';
import { useImageTypeMetadataContext } from './context/ImageTypeMetadataContext';
import { ImageToRulerLengthAllocations } from './helpers/image-to-ruler-allocation';
import { calculateHeightInPxForLaneThickness } from './hooks/use-dynamic-viewport';
import { laneRowSizeProvider, LaneThicknessProvider } from './hooks/use-merged-image-type-loaders';
import { LengthRange } from './types/lengthRange';

export interface MultiCoreViewerProps {
	configurations: CoreLaneRowConfiguration[][];
	isLoading: boolean;
	vertical?: boolean;
	allocations: ImageToRulerLengthAllocations;
	mergedThickness?: LaneThicknessProvider;
	tooltip?: (viewerWidth: number) => React.ReactElement | undefined;
	onScaleFactorChange?: (scale: number) => void;
	onLaneMouseHover?: (at?: number, atTray?: number) => void;
	intervals: IntervalsData;
	onIntervalUpdating?: UpdateIntervalFn;
	onIntervalCreating?: CreateIntervalFn;
	allowableRanges?: LengthRange[];
	crosshairDepthM?: number;
	canLoadImages: boolean;
}

export const MultiCoreViewer: React.FC<MultiCoreViewerProps> = (props) => {
	const {
		vertical = false,
		allocations,
		tooltip,
		configurations,
		isLoading,
		onLaneMouseHover,
		intervals,
		onIntervalCreating,
		onIntervalUpdating,
		allowableRanges,
		crosshairDepthM,
	} = props;

	const { scale } = useCoreViewerContext();

	const { getOrLoadImageType, loaders } = useImageTypeMetadataContext();
	const mergedThicknessInPx = useMemo(
		() =>
			scale
				? Math.max(
						...(configurations
							.map((c) =>
								c
									.map((ic) => laneRowSizeProvider(ic, loaders, scale))
									.filter((ic) => !ic?.thickness.threshold || scale > ic.thickness.threshold),
							)
							.map((t) =>
								t.reduce(
									(prev, curr) =>
										prev +
										(curr?.thickness
											? calculateHeightInPxForLaneThickness(curr.thickness, scale ?? 0)
											: 0),
									0,
								),
							) ?? []),
					)
				: 0,
		[configurations, loaders, scale],
	);

	const [laneMouseHoverInternal, setLaneMouseHoverInternal] = useState<number>();

	const onLaneMouseHoverInternal = useCallback(
		(at?: number) => {
			setLaneMouseHoverInternal(at);
			onLaneMouseHover?.(at);
		},
		[onLaneMouseHover],
	);

	useEffect(() => {
		configurations.forEach((config) => {
			config.forEach((ic) => {
				if (ic.type === CoreLaneRowType.Image) getOrLoadImageType(ic.imageTypeId);
			});
		});
	}, [configurations, getOrLoadImageType]);

	const allowOverlapSetIds = useMemo(
		() =>
			configurations
				.flat()
				.filter(
					(c): c is IntervalLaneRowConfiguration =>
						c.type === CoreLaneRowType.Interval && !!(c as IntervalLaneRowConfiguration).allowOverlap,
				)
				.map((c) => c.intervalSetId),
		[configurations],
	);

	const maxOverlapCounts = useMemo(
		() =>
			Object.fromEntries(
				configurations
					.flat()
					.filter(
						(c): c is IntervalLaneRowConfiguration =>
							c.type === CoreLaneRowType.Interval &&
							!!(c as IntervalLaneRowConfiguration).allowOverlap &&
							(c as IntervalLaneRowConfiguration).maxOverlapCount !== undefined,
					)
					.map((c) => [c.intervalSetId, c.maxOverlapCount!]),
			),
		[configurations],
	);

	return (
		<div className={clsx(styles.multiViewContainer, { [styles.vertical]: vertical })}>
			<IntervalDataProvider
				intervals={intervals}
				onIntervalCreating={onIntervalCreating}
				onIntervalUpdating={onIntervalUpdating}
			>
				<IntervalLaneRowProvider
					mousePosition={laneMouseHoverInternal ?? 0}
					allowableRanges={allowableRanges}
					allowOverlapSetIds={allowOverlapSetIds}
					maxOverlapCounts={maxOverlapCounts}
				>
					{!isLoading &&
						configurations.map((configuration, i) => {
							return (
								loaders &&
								allocations && (
									<CoreViewer
										key={`viewer-${i}`}
										viewerId={`viewer-${i}`}
										loaders={loaders}
										allocations={allocations}
										mergedThicknessInPx={mergedThicknessInPx}
										tooltip={tooltip}
										isPrimary={i === 0}
										configurations={configuration}
										onLaneMouseHover={onLaneMouseHoverInternal}
										allowableRanges={allowableRanges}
										crosshairDepthM={crosshairDepthM}
									/>
								)
							);
						})}
					{!isLoading && configurations.length === 0 && (
						<div className={styles.loadingNotice}>No image selected</div>
					)}
					{isLoading && <div className={styles.loadingNotice}>Loading Drillhole Data...</div>}
				</IntervalLaneRowProvider>
			</IntervalDataProvider>
		</div>
	);
};
